import { Required } from '../components/Required';
import { RowActions } from '../components/RowActions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Loader2, Plus, Smartphone, Zap } from 'lucide-react';
import { useState } from 'react';
import { formatMeterNumber, refuseMeterNumber } from '@jt/domain';
import { api } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';
import { EmptyState } from '../components/EmptyState';
import { AssignDialog, CorrectDialog, SendDialog } from '../components/ForwardedInbox';
import { PowerActivity } from '../components/PowerActivity';
import { LandlordDetails } from '../components/LandlordDetails';
import { Filters, useFilters, type FilterField } from '../components/Filters';
import { SelectionBar, type BulkAction } from '../components/SelectionBar';
import { HelpHint } from '../components/HelpHint';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { PageNav } from '../components/PageNav';
import { useSession } from '../lib/session';
import type {
  PowerActivity as PowerActivityRow,
  PowerMeter,
  SettingsPayload,
} from '../lib/types';

/**
 * The landlords' prepaid meters, and the tokens bought for them.
 *
 * ## Why this screen exists at all
 *
 * The whole feature — meters, budgets, batching, the optical-character work
 * that reads a token out of a KPLC SMS, forwarding it to the landlord, 48 unit
 * tests — shipped with no user interface whatsoever. Every part of it was
 * reachable by an API call and by nothing else, which for the person who has
 * to pay forty meters on the first of the month is the same as not existing.
 *
 * ## Paying is a person with a phone, and that is the design
 *
 * Sending money out through Safaricom is a different product from taking it in:
 * B2B needs an initiator name, a security credential, and an approval applied
 * for separately. Until that exists, the honest flow is the one the business
 * already runs — somebody pays from their own M-Pesa, reads the token off the
 * reply, and passes it on.
 *
 * So this screen does not pretend to pay. It works out what each meter needs,
 * records the intent, and then gets out of the way with the three things
 * required to do it by hand: the paybill, the account number (which is the
 * meter number), and the amount. Pasting the KPLC message back closes the loop
 * and the landlord is messaged automatically.
 *
 * One at a time or all at once, through the same call — because a record of a
 * one-off payment has to add up with the rest at the end of the month.
 */
export function PowerPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingMeter, setEditingMeter] = useState<PowerMeter | null>(null);

  /*
   * Deleting asks first, and switching off does not.
   *
   * One is reversible and keeps every payment; the other cannot be undone.
   * Confirming both would train people to dismiss the question, which is how
   * a confirmation stops being one.
   */
  const [deletingMeter, setDeletingMeter] = useState<PowerMeter | null>(null);
  const [viewing, setViewing] = useState<PowerMeter | null>(null);

  /** A payment somebody is correcting, and one they are closing off. */
  const [correcting, setCorrecting] = useState<PowerActivityRow | null>(null);
  const [failing, setFailing] = useState<PowerActivityRow | null>(null);

  /**
   * A message somebody wants to act on, by id.
   *
   * The three dialogs for a forwarded message need the whole message — its
   * body, its units, what was read out of it — and an activity row carries
   * only what a table column needs. So a row hands over an id and the message
   * is looked up, rather than widening every row in the list with fields that
   * three dialogs read and nothing else does.
   */
  const [messageAction, setMessageAction] = useState<{
    kind: 'assign' | 'correct' | 'send';
    id: string;
  } | null>(null);

  const meters = useQuery({ queryKey: ['power-meters'], queryFn: api.powerMeters });
  const plan = useQuery({ queryKey: ['power-plan'], queryFn: () => api.powerPlan(false) });

  /* Only fetched while a message dialog is open, which is rarely. */
  const messages = useQuery({
    queryKey: ['forwarded-messages', 'all'],
    queryFn: () => api.forwardedMessages({ state: 'all' }),
    enabled: messageAction !== null,
  });

  const chosenMessage =
    messageAction && (messages.data ?? []).find((one) => one.id === messageAction.id);

  const refresh = async () => {
    setError(null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['power-meters'] }),
      queryClient.invalidateQueries({ queryKey: ['power-plan'] }),
      queryClient.invalidateQueries({ queryKey: ['power-outstanding'] }),
    ]);
  };

  /*
   * The phone that is going to pay.
   *
   * Kept on the screen between batches, because in practice it is the same
   * handset all afternoon — the one sitting on the desk with the forwarding
   * app running. Asking for it once and remembering it is the difference
   * between a prompt and an interrogation.
   *
   * `localStorage` rather than state: whoever does this comes back to the
   * screen tomorrow and it is still the same phone.
   */
  const [chosenMeters, setChosenMeters] = useState<Set<string>>(new Set());

  /**
   * What can be done to several meters at once.
   *
   * Switching one off is the only state a meter has, and it is reversible —
   * a meter that is no longer ours stops being topped up and keeps every
   * purchase ever made against it, because those are payments that really
   * happened. There is no delete, for the same reason there is none on an
   * invoice.
   */
  const METER_FIELDS: FilterField<PowerMeter>[] = [
    { key: 'label', label: 'Meter', type: 'text', value: (m) => m.label },
    { key: 'number', label: 'Meter number', type: 'text', value: (m) => m.meterNumberFormatted },
    { key: 'landlord', label: 'Landlord', type: 'text', value: (m) => m.landlordName },
    { key: 'phone', label: 'Phone', type: 'text', value: (m) => m.landlordPhone },
    { key: 'site', label: 'Site', type: 'text', value: (m) => m.siteLabel },
    { key: 'paid', label: 'Last paid', type: 'number', value: (m) => (m.lastPaidCents === null ? null : m.lastPaidCents / 100) },
    {
      key: 'active',
      label: 'Topped up',
      type: 'choice',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      value: (m) => (m.isActive ? 'yes' : 'no'),
    },
  ];

  const meterActions: BulkAction<PowerMeter>[] = [
    { key: 'print', label: 'Print', prominent: true, run: () => window.print() },
    {
      key: 'off',
      label: 'Stop topping up',
      confirm: (picked) =>
        `Stop topping up ${picked.length} meter${picked.length === 1 ? '' : 's'}? ` +
        'Every purchase already made against them stays exactly as it is.',
      run: async (picked) => {
        for (const meter of picked) await api.updatePowerMeter(meter.id, { isActive: false });
        setChosenMeters(new Set());
        await refresh();
      },
    },
    {
      key: 'on',
      label: 'Start topping up again',
      run: async (picked) => {
        for (const meter of picked) await api.updatePowerMeter(meter.id, { isActive: true });
        setChosenMeters(new Set());
        await refresh();
      },
    },
  ];

  const meterColumns: Column<PowerMeter>[] = [
    {
      key: 'label',
      header: 'Meter',
      sortBy: (meter) => meter.label.toLowerCase(),
      cell: (meter) => (
        <span className={meter.isActive ? '' : 'opacity-60'}>
          <span className="font-medium">{meter.label}</span>
          <span className="block text-xs text-muted-foreground">{meter.meterNumberFormatted}</span>
          {!meter.isActive && (
            <span className="mt-0.5 inline-block rounded bg-muted px-1.5 py-0.5 text-xs">
              not topped up
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'landlord',
      header: 'Landlord',
      sortBy: (meter) => meter.landlordName.toLowerCase(),
      cell: (meter) => (
        <span>
          {meter.landlordName}
          <span className="block text-xs text-muted-foreground">{meter.landlordPhone}</span>
        </span>
      ),
    },
    {
      key: 'site',
      header: 'Site',
      secondary: true,
      sortBy: (meter) => meter.siteLabel ?? null,
      cell: (meter) => <span className="text-muted-foreground">{meter.siteLabel ?? '—'}</span>,
    },
    /*
     * What was actually paid, not a budget typed in once: the amount of the
     * last payment that took money, and when. The next one falls due on the
     * same date the following month.
     */
    {
      key: 'paid',
      header: 'Last paid',
      numeric: true,
      // On the number behind the formatted figure, not on its text.
      sortBy: (meter) => meter.lastPaidCents ?? null,
      cell: (meter) =>
        meter.lastPaid ? (
          <span className="tabular-nums">
            {meter.lastPaid}
            <span className="block text-xs text-muted-foreground">{meter.since}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">Not paid yet</span>
        ),
    },
    {
      key: 'due',
      header: 'Next due',
      sortBy: (meter) => meter.nextDueAt ?? null,
      cell: (meter) => <DueCell meter={meter} />,
    },
    {
      key: 'since',
      header: 'Last topped up',
      secondary: true,
      sortBy: (meter) => meter.lastPurchasedAt ?? null,
      cell: (meter) => <span className="text-muted-foreground">{meter.since}</span>,
    },
    /*
     * Editing was reachable only through the bulk bar, which could switch a
     * meter off and nothing else — so a landlord's name or phone number typed
     * wrongly stayed wrong, and the phone number is where the token goes.
     */
    ...(can('subscription:write')
      ? [
          {
            key: 'edit',
            header: '',
            /*
             * One press for the usual thing, the rest behind the menu.
             *
             * Switching off and deleting sound like alternatives and are not:
             * one is reversible and keeps every payment, the other is for a
             * row that should never have existed. Putting them side by side
             * at the same weight is how the wrong one gets pressed, so the
             * destructive one is last and separated.
             */
            cell: (meter: PowerMeter) => (
              <RowActions
                actions={[
                  {
                    label: 'View details',
                    primary: true,
                    title: 'Everything bought for this meter, and the landlord',
                    onClick: () => setViewing(meter),
                  },
                  { label: 'Edit', onClick: () => setEditingMeter(meter) },
                  {
                    label: meter.isActive ? 'Switch off' : 'Switch on',
                    title: meter.isActive
                      ? 'Stop topping this meter up. Nothing is lost — every payment stays.'
                      : 'Start topping this meter up again',
                    onClick: () => toggleMeter.mutate(meter),
                  },
                  {
                    label: 'Delete',
                    danger: true,
                    title:
                      'Only for a meter nothing has been bought for. Otherwise switch it off.',
                    onClick: () => setDeletingMeter(meter),
                  },
                ]}
              />
            ),
          } satisfies Column<PowerMeter>,
        ]
      : []),
  ];

  const toggleMeter = useMutation({
    mutationFn: (meter: PowerMeter) =>
      api.updatePowerMeter(meter.id, { isActive: !meter.isActive }),
    onSuccess: refresh,
  });

  const removeMeter = useMutation({
    mutationFn: (id: string) => api.removePowerMeter(id),
    onSuccess: async () => {
      setDeletingMeter(null);
      await refresh();
    },
  });

  const allMeters = meters.data ?? [];
  const {
    query: meterQuery,
    setQuery: setMeterQuery,
    clauses: meterClauses,
    setClauses: setMeterClauses,
    filtered: rows,
  } = useFilters(allMeters, METER_FIELDS);

  if (meters.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  return (
    <div className="space-y-6">
      <PageNav crumbs={[{ label: 'Power & tokens' }]} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-1.5 font-heading text-2xl">
            Power &amp; tokens
            <HelpHint>
              GoSpeedNet pays the electricity on the landlords&rsquo; meters that carry our
              equipment. Payments are made from the app on the paying phone, and the Kenya Power
              token goes on to the landlord.
            </HelpHint>
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {plan.data?.summary ?? `${rows.length} meter${rows.length === 1 ? '' : 's'}.`}
          </p>
        </div>
        {can('subscription:write') && (
          <button type="button" className="btn-secondary" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add a meter
          </button>
        )}
      </div>

      {error && (
        <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      {/* ── Every meter ─────────────────────────────────────────────────── */}
      <section className="card">
        <h2 className="font-heading text-lg">All meters</h2>
        {rows.length === 0 ? (
          <EmptyState
            icon={Zap}
            headline="No meters yet"
            body="Add the landlords' meters that GoSpeedNet pays the power on."
          />
        ) : (
          <>
            <Filters<PowerMeter>
              fields={METER_FIELDS}
              query={meterQuery}
              onQuery={setMeterQuery}
              clauses={meterClauses}
              onClauses={setMeterClauses}
              total={allMeters.length}
              showing={rows.length}
              placeholder="Search by meter, landlord or site"
            />

            <SelectionBar<PowerMeter>
              rows={rows}
              selected={chosenMeters}
              onClear={() => setChosenMeters(new Set())}
              rowKey={(meter) => meter.id}
              exportName="power-meters"
              csv={[
                { header: 'Meter', value: (m) => m.label },
                { header: 'Number', value: (m) => m.meterNumberFormatted },
                { header: 'Landlord', value: (m) => m.landlordName },
                { header: 'Phone', value: (m) => m.landlordPhone },
                { header: 'Site', value: (m) => m.siteLabel ?? '' },
                { header: 'Last paid', value: (m) => m.lastPaid ?? '' },
                { header: 'Next due', value: (m) => m.nextDue ?? '' },
                { header: 'Last topped up', value: (m) => m.since },
                { header: 'In use', value: (m) => (m.isActive ? 'yes' : 'no') },
              ]}
              actions={meterActions}
            />

            <DataTable<PowerMeter>
              rows={rows}
              columns={meterColumns}
              rowKey={(meter) => meter.id}
              selected={can('subscription:write') ? chosenMeters : undefined}
              onSelect={can('subscription:write') ? setChosenMeters : undefined}
              defaultSort={{ key: 'label' }}
              caption={`${rows.length} ${rows.length === 1 ? 'meter' : 'meters'}`}
              empty={<p className="py-6 text-center text-sm text-muted-foreground">No meters.</p>}
            />
          </>
        )}
      </section>

      {/*
        * One table for both halves.
        *
        * Payments and forwarded messages were two panels because they live in
        * two tables — a fact about the database, and of no use to anybody
        * asking what happened to a meter this afternoon. They sorted
        * differently, filtered differently, offered different verbs, and the
        * order of events had to be held in somebody's head.
        */}

      <PowerActivity
        onLandlord={(row) => {
          const meter = allMeters.find((one) => one.id === row.meterId);
          if (meter) setViewing(meter);
        }}
        onEdit={(row) => setCorrecting(row)}
        onFail={(row) => setFailing(row)}
        onAssign={(row) => setMessageAction({ kind: 'assign', id: row.id })}
        onCorrect={(row) => setMessageAction({ kind: 'correct', id: row.id })}
        onSend={(row) => {
          /*
           * A token can be sent from either table it landed in. A forwarded
           * message opens the dialog that shows what arrived; a purchase has
           * no message to show, so it goes through the landlord's own page,
           * which is where every token that meter has had already lives.
           */
          if (row.source === 'message') {
            setMessageAction({ kind: 'send', id: row.id });
            return;
          }
          const meter = allMeters.find((one) => one.id === row.meterId);
          if (meter) setViewing(meter);
        }}
      />

      {/*
        * The handset, in one folded card: the app to install and the pairing
        * and triggers that link it here. Opened when a phone is being set up,
        * not read every day.
        */}
      {can('payment:record') && <PayingPhoneSetup mayInstall={can('settings:write')} />}

      {adding && <AddMeterDialog onDone={refresh} onClose={() => setAdding(false)} />}

      {viewing && <LandlordDetails meterId={viewing.id} onClose={() => setViewing(null)} />}

      {correcting && (
        <EditPurchaseDialog
          purchase={correcting}
          onDone={async () => {
            setCorrecting(null);
            await refresh();
          }}
          onClose={() => setCorrecting(null)}
        />
      )}

      {messageAction?.kind === 'assign' && chosenMessage && (
        <AssignDialog
          message={chosenMessage}
          meters={allMeters}
          onDone={refresh}
          onClose={() => setMessageAction(null)}
        />
      )}

      {messageAction?.kind === 'correct' && chosenMessage && (
        <CorrectDialog
          message={chosenMessage}
          onDone={refresh}
          onClose={() => setMessageAction(null)}
        />
      )}

      {messageAction?.kind === 'send' && chosenMessage && (
        <SendDialog
          message={chosenMessage}
          onDone={refresh}
          onClose={() => setMessageAction(null)}
          onError={setError}
        />
      )}

      {failing && (
        <MarkFailedDialog
          purchase={failing}
          onDone={async () => {
            setFailing(null);
            await refresh();
          }}
          onClose={() => setFailing(null)}
        />
      )}

      {/*
        Asked, and told what it will refuse.

        The server declines to delete a meter anything has been bought for,
        because that would leave money with nothing to say what it bought.
        Saying so before the press is better than an error after it — and it
        is also the moment to name the reversible alternative.
      */}
      {deletingMeter && (
        <Modal
          title={`Delete ${deletingMeter.label}?`}
          subtitle={`Meter ${deletingMeter.meterNumberFormatted}`}
          onClose={() => setDeletingMeter(null)}
          footer={
            <>
              <button
                type="button"
                className="btn-quiet"
                onClick={() => setDeletingMeter(null)}
              >
                Keep it
              </button>
              <button
                type="button"
                className="btn-primary bg-danger-500 hover:bg-danger-600"
                disabled={removeMeter.isPending}
                onClick={() => removeMeter.mutate(deletingMeter.id)}
              >
                {removeMeter.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                Delete it
              </button>
            </>
          }
        >
          <p className="text-sm">
            This cannot be undone. It is refused if anything has been bought for this meter,
            because a payment with no meter behind it is an unanswerable question in the accounts.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            To stop topping it up while keeping its history, close this and choose{' '}
            <strong className="text-foreground">Switch off</strong> instead.
          </p>
          {removeMeter.isError && (
            <p className="mt-3 rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
              {(removeMeter.error as Error).message}
            </p>
          )}
        </Modal>
      )}

      {editingMeter && (
        <EditMeterDialog
          meter={editingMeter}
          onDone={refresh}
          onClose={() => setEditingMeter(null)}
        />
      )}
    </div>
  );
}

/**
  const { label, look } =
    row.status === 'failed'
      ? { label: 'Failed', look: 'border-danger-300 bg-danger-50 text-danger-700' }
      : row.stages.sent.done || row.stages.sent.queued
        ? { label: 'Sent', look: 'border-secondary-300 bg-secondary-50 text-secondary-900' }
        : row.stages.token.done
          ? { label: 'Token in', look: 'border-primary-300 bg-primary-50 text-primary-900' }
          : row.stages.paid.done
            ? { label: 'Paid', look: 'border-primary-300 bg-primary-50 text-primary-900' }
            : { label: 'Waiting', look: 'border-border bg-muted text-muted-foreground' };

  return (
    <span className={`rounded-full border px-2 py-0.5 text-xs font-normal ${look}`}>{label}</span>
  );
}

/**
 * Correcting a payment before the money moves.
 *
 * Two fields, because two are all that can be wrong and still fixable: the
 * amount, and the phone that is going to pay. The meter is not here on
 * purpose — a purchase pointed at a different meter is a different purchase,
 * and editing it would move money between landlords with nothing in the audit
 * trail to explain it.
 *
 * Saving does not re-send anything. The instructions already went to a
 * handset, so the person decides whether the corrected ones are worth sending
 * again — which is what Retry is for, and what the closing line says.
 */
/** What both dialogs actually read, so either list can open them. */
type PurchaseLike = {
  id: string;
  meterLabel: string | null;
  meterNumber: string | null;
  landlordName: string | null;
  amountCents: number | null;
  payerPhone: string | null;
};

function EditPurchaseDialog({
  purchase,
  onDone,
  onClose,
}: {
  purchase: PurchaseLike;
  onDone: () => void;
  onClose: () => void;
}) {
  const [amountKes, setAmountKes] = useState(() =>
    String(Math.round((purchase.amountCents ?? 0) / 100)),
  );
  const [payerPhone, setPayerPhone] = useState(purchase.payerPhone ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.editPowerPurchase(purchase.id, {
        amountCents: Math.round(Number(amountKes || '0') * 100),
        payerPhone,
      }),
    onSuccess: onDone,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      title={`Correct ${purchase.meterLabel}`}
      subtitle={`Meter ${purchase.meterNumber} · for ${purchase.landlordName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Save
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
            {error}
          </p>
        )}

        <label className="block text-sm">
          Amount (KES)
          <input
            className="input mt-1 w-full"
            inputMode="numeric"
            value={amountKes}
            aria-label="The amount"
            onChange={(event) => setAmountKes(event.target.value)}
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            Below Kenya Power&rsquo;s floor they take the money and issue no token, which is the
            worst outcome available: paid, dark, and nothing that looks like an error.
          </span>
        </label>

        <label className="block text-sm">
          Paying from
          <input
            className="input mt-1 w-full"
            inputMode="tel"
            value={payerPhone}
            aria-label="The paying phone"
            onChange={(event) => setPayerPhone(event.target.value)}
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            The prompt goes here and the token comes back from here. Both follow the number, so a
            wrong one takes the whole chain with it.
          </span>
        </label>

        <p className="text-xs text-muted-foreground">
          Saving changes the payment but sends nothing. The old instructions have already reached a
          handset &mdash; press <strong className="text-foreground">Retry</strong> when you want the
          corrected ones to go.
        </p>
      </div>
    </Modal>
  );
}

/**
 * Closing off a payment that is never going to complete.
 *
 * ## Why this needs a reason, and why it is not a delete
 *
 * A purchase that sits in flight forever is worse than one marked failed: it
 * keeps a spinner turning on a screen somebody checks, and it keeps the meter
 * out of "due now" as though it had been paid for. But the money may genuinely
 * have left — a handset payment that Safaricom took and KPLC never answered —
 * so the row is not removed. It is marked failed with a sentence somebody
 * wrote, and that sentence is what the month is reconciled against.
 *
 * The API refuses this on a delivered purchase, so a token that did arrive
 * cannot be written off afterwards.
 */
function MarkFailedDialog({
  purchase,
  onDone,
  onClose,
}: {
  purchase: PurchaseLike;
  onDone: () => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fail = useMutation({
    mutationFn: () => api.failPowerPurchase(purchase.id, reason),
    onSuccess: onDone,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      title={`Close off ${purchase.meterLabel}`}
      subtitle={`Meter ${purchase.meterNumber} · KES ${Math.round((purchase.amountCents ?? 0) / 100).toLocaleString('en-KE')} · for ${purchase.landlordName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={reason.trim().length < 3 || fail.isPending}
            title={
              reason.trim().length < 3
                ? 'Say what went wrong first — this is what the month is reconciled against.'
                : 'Mark this payment failed'
            }
            onClick={() => fail.mutate()}
          >
            {fail.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Record the failure
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
            {error}
          </p>
        )}

        <p className="text-sm text-muted-foreground">
          This takes it off the in-flight list without pretending a token arrived. The row stays,
          because the money may really have left — what is written here is what the month is
          reconciled against.
        </p>

        <label className="block text-sm">
          What went wrong
          <textarea
            className="input mt-1 h-24 w-full"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="The prompt was cancelled on the handset and the money never left."
          />
        </label>
      </div>
    </Modal>
  );
}

/**
 * Adding a meter, with the two facts that have to be right.
 *
 * The meter number is the account number every payment uses, so a wrong one
 * credits somebody else's power; the landlord's phone is where the token goes.
 * Everything else here can be corrected later from Edit without anybody
 * losing money.
 */
function AddMeterDialog({ onDone, onClose }: { onDone: () => void; onClose: () => void }) {
  const [form, setForm] = useState({
    label: '',
    meterNumber: '',
    landlordName: '',
    landlordPhone: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);

  const review = () => {
    const problem = meterFormProblem(form);
    setError(problem);
    if (!problem) setReviewing(true);
  };

  const add = useMutation({
    mutationFn: () =>
      api.addPowerMeter({
        label: form.label,
        meterNumber: form.meterNumber,
        landlordName: form.landlordName,
        landlordPhone: form.landlordPhone,
      }),
    onSuccess: async () => {
      await onDone();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm({ ...form, [key]: event.target.value });

  return (
    <Modal
      title={reviewing ? 'Check the meter details' : 'Add a meter'}
      onClose={onClose}
      footer={
        reviewing ? (
          <>
            <button type="button" className="btn-quiet" disabled={add.isPending} onClick={() => setReviewing(false)}>
              Go back and change
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={add.isPending}
              onClick={() => add.mutate()}
            >
              {add.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Complete
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn-quiet" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={review}>
              Check details
            </button>
          </>
        )
      }
    >
      {reviewing ? (
        <div className="space-y-3">
          {error && (
            <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
              {error}
            </p>
          )}
          <MeterReview
            rows={[
              { label: 'Name', value: form.label.trim() },
              { label: 'Meter number', value: formatMeterNumber(form.meterNumber), mono: true },
              { label: 'Landlord', value: form.landlordName.trim() },
              { label: 'Their phone', value: form.landlordPhone.trim(), mono: true },
            ]}
          />
        </div>
      ) : (
      <div className="space-y-3">
        {error && (
          <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
            {error}
          </p>
        )}
        <label className="block text-sm">
          What to call it
          <Required />
          <input
            className="input mt-1 w-full"
            value={form.label}
            onChange={set('label')}
            required
            placeholder="Keumbu tower — Mr Ondieki"
          />
        </label>
        <label className="block text-sm">
          Meter number
          <Required />
          <input
            className="input mt-1 w-full font-mono"
            value={form.meterNumber}
            onChange={set('meterNumber')}
            required
            placeholder="12345678901"
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            8 to 13 digits, off the meter itself (old token meters have 8, most have 11). This is
            the account number every payment uses, so a wrong one credits somebody else&rsquo;s power.
          </span>
        </label>
        <label className="block text-sm">
          Landlord
          <Required />
          <input
            className="input mt-1 w-full"
            value={form.landlordName}
            onChange={set('landlordName')}
            placeholder="Joseph Ondieki"
          />
        </label>
        <label className="block text-sm">
          Their phone
          <Required />
          <input
            className="input mt-1 w-full"
            value={form.landlordPhone}
            onChange={set('landlordPhone')}
            placeholder="0712 345 678"
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            Where the token is sent. It has to be right.
          </span>
        </label>
      </div>
      )}
    </Modal>
  );
}

/** When the next monthly payment falls due, coloured by how close it is. */
function DueCell({ meter }: { meter: PowerMeter }) {
  if (!meter.nextDue) return <span className="text-muted-foreground">—</span>;
  const look: Record<PowerMeter['dueState'], { text: string; className: string }> = {
    never: { text: '', className: 'text-muted-foreground' },
    paid: { text: '', className: 'text-muted-foreground' },
    due_soon: { text: 'Due soon', className: 'border-amber-300 bg-amber-50 text-amber-900' },
    due_today: { text: 'Due today', className: 'border-action-600 bg-action-500 text-action-ink' },
    overdue: { text: 'Overdue', className: 'border-danger-300 bg-danger-50 text-danger-700' },
  };
  const state = look[meter.dueState];
  return (
    <span className="tabular-nums">
      {meter.nextDue}
      {state.text && <span className={`badge ml-2 border ${state.className}`}>{state.text}</span>}
    </span>
  );
}

/**
 * The details as they are about to be saved, read back before anything is.
 *
 * A meter number is copied off a meter in a dark stairwell and a landlord's
 * phone is typed from what somebody said out loud. One wrong digit in either
 * sends the power or the token to a stranger, so saving takes a second look:
 * the form, then this, then Complete.
 */
function MeterReview({ rows }: { rows: { label: string; value: string; mono?: boolean }[] }) {
  return (
    <div className="space-y-3">
      <p className="rounded-xl border border-navy-200 bg-navy-50 p-3 text-sm text-navy-900">
        Check these against the meter and the landlord before you complete. A wrong meter number
        pays for somebody else&rsquo;s power; a wrong phone sends the token to a stranger.
      </p>
      <dl className="divide-y divide-navy-100 rounded-xl border border-navy-100">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-4 px-3 py-2 text-sm">
            <dt className="text-muted-foreground">{row.label}</dt>
            <dd className={row.mono ? 'font-mono tabular-nums' : ''}>{row.value || '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** What is wrong with the form before it can be reviewed, or null. */
function meterFormProblem(form: { label: string; meterNumber: string; landlordName: string; landlordPhone: string }) {
  if (form.label.trim().length < 2) return 'Give the meter a name somebody will recognise.';
  const meter = refuseMeterNumber(form.meterNumber);
  if (meter) return meter;
  if (form.landlordName.trim().length < 2) return 'Whose meter is it?';
  if (form.landlordPhone.trim() === '') return 'The landlord needs a phone number: it is where the token goes.';
  return null;
}

/**
 * Correcting a meter, and the landlord behind it.
 *
 * ## Why this had to exist
 *
 * Six facts were asked for when a meter was added and none of them could be
 * changed afterwards except whether it was topped up. A landlord's phone
 * number typed wrongly stayed wrong, and that number is where the token goes —
 * so a single mistyped digit meant every token for that meter went to a
 * stranger, forever, with no way to put it right short of a developer.
 *
 * ## Why there is no delete
 *
 * Every token purchase ever made against a meter is money that really left the
 * business, and those rows name this one. Switching it off takes it out of the
 * run and leaves all of that where it is, which is what "removing" a meter
 * actually means.
 */
function EditMeterDialog({
  meter,
  onDone,
  onClose,
}: {
  meter: PowerMeter;
  onDone: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    label: meter.label,
    meterNumber: meter.meterNumber,
    landlordName: meter.landlordName,
    landlordPhone: meter.landlordPhone,
    notes: meter.notes ?? '',
    isActive: meter.isActive,
  });
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);

  const review = () => {
    const problem = meterFormProblem(form);
    setError(problem);
    if (!problem) setReviewing(true);
  };

  const save = useMutation({
    mutationFn: () =>
      api.updatePowerMeter(meter.id, {
        label: form.label,
        meterNumber: form.meterNumber,
        landlordName: form.landlordName,
        landlordPhone: form.landlordPhone,
        notes: form.notes,
        isActive: form.isActive,
      }),
    onSuccess: async () => {
      await onDone();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm({ ...form, [key]: event.target.value });

  return (
    <Modal
      title={reviewing ? 'Check the meter details' : `Edit ${meter.label}`}
      onClose={onClose}
      footer={
        reviewing ? (
          <>
            <button type="button" className="btn-quiet" disabled={save.isPending} onClick={() => setReviewing(false)}>
              Go back and change
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Complete
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn-quiet" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={review}>
              Check details
            </button>
          </>
        )
      }
    >
      {reviewing ? (
        <div className="space-y-3">
          {error && (
            <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
              {error}
            </p>
          )}
          <MeterReview
            rows={[
              { label: 'Name', value: form.label.trim() },
              { label: 'Meter number', value: formatMeterNumber(form.meterNumber), mono: true },
              { label: 'Landlord', value: form.landlordName.trim() },
              { label: 'Their phone', value: form.landlordPhone.trim(), mono: true },
              { label: 'Topping up', value: form.isActive ? 'Yes' : 'No, switched off' },
            ]}
          />
        </div>
      ) : (
      <div className="space-y-3">
        {error && (
          <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
            {error}
          </p>
        )}

        <label className="block text-sm">
          What to call it
          <Required />
          <input className="input mt-1 w-full" value={form.label} onChange={set('label')} />
        </label>

        <label className="block text-sm">
          Meter number
          <Required />
          <input
            className="input mt-1 w-full font-mono"
            value={form.meterNumber}
            onChange={set('meterNumber')}
            required
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            The account number every payment uses. Correcting it also lets forwarded tokens that
            name it match on their own.
          </span>
        </label>

        <label className="block text-sm">
          Landlord
          <Required />
          <input
            className="input mt-1 w-full"
            value={form.landlordName}
            onChange={set('landlordName')}
          />
        </label>

        <label className="block text-sm">
          Their phone
          <Required />
          <input
            className="input mt-1 w-full"
            value={form.landlordPhone}
            onChange={set('landlordPhone')}
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            Where the token is sent. Tokens already sent went to the old number &mdash; this changes
            where the next one goes.
          </span>
        </label>


        <label className="block text-sm">
          Notes
          <textarea className="input mt-1 w-full" rows={2} value={form.notes} onChange={set('notes')} />
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
          />
          Keep topping this meter up
        </label>
      </div>
      )}
    </Modal>
  );
}

/**
 * Connecting the phone that pays, so the token forwards itself.
 *
 * Kenya Power has no API to ask for a token — it arrives by SMS on whichever
 * handset paid, and that is the only way it is ever delivered. So the only
 * route that does not involve somebody retyping it starts on that phone: a
 * forwarding app posts the message here, the meter number in it picks the
 * right purchase, and the landlord is sent the token automatically.
 *
 * ## Both senders, and why this screen now insists
 *
 * It used to say "forward only messages from KPLC", which was right when the
 * token was taken at face value and became wrong the moment it stopped being.
 * The checks that make a forwarded token safe — that the money matches, that
 * the receipt matches, that it arrived after the payment rather than three
 * months earlier — all need Safaricom's confirmation as well. Following the
 * old instruction would have switched most of them off without saying so,
 * which is the worst way for a safeguard to fail.
 *
 * The URL is the credential — the secret is in the path, because most Android
 * forwarding apps let you set a URL and nothing else. Rotating it is the answer
 * to a lost phone and takes one press.
 */
/**
 * The delivery half: the same handset sends the token on.
 *
 * The card above sets up the phone talking *to* us. This sets up the phone
 * talking *for* us — the route that needs no gateway account, no approved
 * sender ID and nobody's allowlist, which is why it is here rather than in a
 * document somebody would have to be told about.
 */

/**
 * Where the webhook addresses go, and nowhere else.
 *
 * Both used to sit inside cards that fold shut, beside the instructions for
 * the macro they belong to. That reads well and finds badly: somebody who has
 * already built the macro and only wants to paste an address had to guess
 * which section to expand, and the honest report was "I cannot see where to
 * add the URL".
 *
 * Instructions fold. The box you came to fill does not.
 */
/**
 * The app for the paying handset, and how to get it onto one.
 *
 * ## Why it is downloaded from here rather than the Play Store
 *
 * The app sends SMS and reads them, and both are restricted permissions that
 * Google grants by exemption. A private dispatch tool for one company does not
 * qualify, so self-hosting is not a preference here — it is the only route.
 *
 * ## Why the checksum is on screen
 *
 * It costs nothing, and it is the only way somebody can tell that the file
 * that reached the phone is the file that was built.
 */
function AppBuildCard() {
  const build = useQuery({ queryKey: ['payphone-latest'], queryFn: api.payphoneLatest });

  const size = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  return (
    <div className="mt-3 rounded-xl border border-border p-3">
      <div className="text-sm font-medium">The app for the paying phone</div>

      {build.isLoading && <p className="mt-2 text-sm text-muted-foreground">Checking…</p>}

      {build.data?.available === false && (
        <p className="mt-2 text-sm text-muted-foreground">
          No build has been uploaded yet. Once one is, the link and its checksum appear here.
        </p>
      )}

      {build.data?.build && (
        <div className="mt-2 space-y-2 text-sm text-muted-foreground">
          <p>
            Version <span className="font-mono text-foreground">{build.data.build.version}</span>
            {' · '}
            {size(build.data.build.size)}
            {' · '}
            uploaded {new Date(build.data.build.uploadedAt).toLocaleString()}
          </p>

          {build.data.build.notes && <p>{build.data.build.notes}</p>}

          <p className="break-all">
            SHA-256 <span className="font-mono text-xs">{build.data.build.sha256}</span>
          </p>

          <a className="btn-primary inline-flex" href="/api/v1/payphone/download">
            Download the app
          </a>

          {/*
            * Said on screen rather than left to be discovered.
            *
            * Android refuses the install once per phone until "install unknown
            * apps" is allowed for whichever browser downloaded it, and without
            * this sentence that refusal reads as the app being broken.
            */}
          <p>
            Open this link on the handset. Android will refuse the install the first time until
            you allow <strong>install unknown apps</strong> for the browser you downloaded it
            with. After installing, turn on{' '}
            <strong>Settings → Accessibility → GoSpeedNet Pay</strong>, or the M-PESA menu cannot
            be answered.
          </p>
        </div>
      )}
    </div>
  );
}

function PayingPhoneSetup({ mayInstall }: { mayInstall: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="card" data-paying-phone-setup>
      <h2 className="font-heading text-lg">
        <button
          type="button"
          className="flex w-full items-center gap-2 text-left"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? '' : '-rotate-90'}`}
            aria-hidden="true"
          />
          The app for the paying phone
        </button>
      </h2>
      {open && (
        <>
          {mayInstall && (
            <div className="mt-3">
              <p className="max-w-2xl text-sm text-muted-foreground">
                Install this on the handset that holds the paying SIM. It signs in, pays, and
                sends the Kenya Power token back.
              </p>
              <AppBuildCard />
            </div>
          )}
          <PhoneTriggersCard />
        </>
      )}
    </section>
  );
}

function PhoneTriggersCard() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings });

  const [error, setError] = useState<string | null>(null);
  const [naming, setNaming] = useState<string | null>(null);

  /*
   * Switching is one call, not three saves.
   *
   * A number and its two triggers are one physical phone. Saved separately
   * there is a window in the middle where the number is the test handset and
   * the triggers still point at the live one — which is exactly the state
   * that split the chain this morning, and a browser closed in that window
   * leaves it true for good.
   */
  const usePhone = useMutation({
    mutationFn: (id: string) => api.powerUsePhone(id),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      await queryClient.invalidateQueries({ queryKey: ['power-readiness'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const power = settings.data?.power;

  const save = useMutation({
    mutationFn: (patch: Partial<SettingsPayload['power']>) => api.updateSettings({ power: patch }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      await queryClient.invalidateQueries({ queryKey: ['power-readiness'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!power) return null;

  return (
    <div className="mt-4" data-phone-triggers>
      <h3 className="font-heading text-base">Webhook addresses from the paying phone</h3>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        Treat them as passwords &mdash; anyone holding one can make the phone dial or text at your
        expense.
      </p>

      {error && (
        <p className="mt-3 max-w-2xl rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      {/*
        ── Pairing the app ──────────────────────────────────────────────

        The app's own screen says "ask an administrator for a pairing code
        from Money & stock → Power", and until now there was no such button
        on this screen — the endpoint existed and nothing called it. Nobody
        had ever paired a handset, which is why every payment went through a
        trigger address typed into this page. Both of those addresses are gone
        now; this is the whole of how a handset is set up.
      */}
      <PairingCard />

      {/*
        ── Which handset, before anything about it ──────────────────────

        At the top because it is the question that governs the two boxes
        below: a trigger address is meaningless without knowing which phone
        it belongs to, and reading them in the other order is how somebody
        edits the live handset while thinking they are editing the test one.
      */}
      <div className="mt-4 rounded-xl border border-border p-3">
        <h3 className="font-medium">Which phone is this</h3>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Switching changes the paying number and both triggers together. They describe one
          physical handset, so changing one without the others sends the payment details to one
          phone and the dial instruction to another.
        </p>

        <div className="mt-2 flex flex-wrap gap-2">
          {(power.phones ?? []).map((phone) => {
            const live = phone.id === power.activePhone;
            return (
              <span
                key={phone.id}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm ${
                  live
                    ? 'border-secondary-300 bg-secondary-50 text-secondary-900'
                    : 'border-border bg-muted'
                }`}
              >
                <span>
                  {phone.label}
                  {phone.payerPhone && (
                    <span className="ml-1 font-mono text-xs opacity-70">{phone.payerPhone}</span>
                  )}
                </span>

                {live ? (
                  <span className="text-xs font-medium">In use</span>
                ) : (
                  <button
                    type="button"
                    className="btn-quiet px-2 py-0.5 text-xs"
                    disabled={usePhone.isPending}
                    title={`Switch to ${phone.label} — number and both triggers`}
                    onClick={() => usePhone.mutate(phone.id)}
                  >
                    Use
                  </button>
                )}

                <button
                  type="button"
                  className="text-xs text-danger-700 hover:underline"
                  disabled={save.isPending || live}
                  title={
                    live
                      ? 'Switch to another phone before removing this one.'
                      : 'Forget this phone. The addresses stay in the lists below.'
                  }
                  onClick={() =>
                    save.mutate({
                      phones: (power.phones ?? []).filter((one) => one.id !== phone.id),
                    } as Partial<SettingsPayload['power']>)
                  }
                >
                  Remove
                </button>
              </span>
            );
          })}

          {naming === null ? (
            <button
              type="button"
              className="btn-quiet px-3 py-1 text-sm"
              title="Save the number and triggers below as a phone you can switch back to"
              onClick={() => setNaming('')}
            >
              Save these as a phone
            </button>
          ) : (
            <span className="inline-flex items-center gap-2">
              <input
                className="input py-1 text-sm"
                value={naming}
                placeholder="Testing handset"
                aria-label="A name for this phone"
                autoFocus
                onChange={(event) => setNaming(event.target.value)}
              />
              <button
                type="button"
                className="btn-primary px-3 py-1 text-sm"
                disabled={save.isPending || naming.trim().length === 0}
                onClick={() => {
                  const id = `phone-${Date.now().toString(36)}`;
                  save.mutate({
                    phones: [
                      ...(power.phones ?? []),
                      {
                        id,
                        label: naming.trim(),
                        payerPhone: power.payerPhone,
                        dialWebhookUrl: power.dialWebhookUrl,
                        sendWebhookUrl: power.sendWebhookUrl,
                      },
                    ],
                    /*
                     * Saved as the one in use. Naming what is on screen and
                     * then not being on it would be a trap of its own.
                     */
                    activePhone: id,
                  } as Partial<SettingsPayload['power']>);
                  setNaming(null);
                }}
              >
                Save
              </button>
              <button
                type="button"
                className="btn-quiet px-3 py-1 text-sm"
                onClick={() => setNaming(null)}
              >
                Cancel
              </button>
            </span>
          )}
        </div>

        {(power.phones ?? []).length === 0 && (
          <p className="mt-2 text-xs text-muted-foreground">
            None saved yet. Set the number and triggers below, then save them under a name &mdash;
            a testing handset and a live one, so switching between them is one press.
          </p>
        )}
      </div>

      {/*
        The two MacroDroid trigger addresses stood here: one that dialled the
        paybill, one that texted the token on.

        Both are gone from the screen because the app does each of those
        jobs now — it takes a dial from the queue on its pairing token, and
        the token goes out through the outbox with the business’s sender ID.
        Neither needs an address typed into a settings page.

        The stored values are deliberately left in the database rather than
        deleted. `dispatchMethod` is `app`, and the dial falls back to the
        trigger if no handset is live — so a phone that is off is a slower
        payment rather than no payment. Removing the values would remove
        that floor, and the way back is a settings change rather than a
        restore.
      */}
    </div>
  );
}

/**
 * A code that pairs one handset, once, within ten minutes.
 *
 * Eight characters from a 32-letter alphabet with no I, O, 0 or 1 in it,
 * because this is read off one screen and typed into another by somebody
 * holding both.
 *
 * Shown once and not stored anywhere readable: only its hash is kept, so a
 * code that scrolls away has to be reissued rather than recovered. That is the
 * right trade for something that pairs a device to the business's money.
 */
function PairingCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);

  const devices = useQuery({ queryKey: ['paired-devices'], queryFn: api.pairedDevices });

  const issue = useMutation({
    mutationFn: () => api.issuePairingCode(),
    onSuccess: setCode,
    onError: (error: Error) => toast.failed(error.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeDevice(id),
    onSuccess: () => {
      toast.done('That handset can no longer pay. It would have to be paired again.');
      void queryClient.invalidateQueries({ queryKey: ['paired-devices'] });
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  const paired = devices.data?.devices ?? [];

  return (
    <div className="mt-4 rounded-xl border border-border p-3">
      <h3 className="font-medium">The GoSpeedNet Pay app</h3>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        The app on the paying handset asks for a code from this screen. It lasts ten minutes,
        works once, and is shown here only the once — only its fingerprint is kept, so one that
        scrolls away has to be reissued rather than looked up.
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="btn-secondary"
          disabled={issue.isPending}
          onClick={() => issue.mutate()}
        >
          {issue.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Smartphone className="h-4 w-4" aria-hidden="true" />
          )}
          Pair a handset
        </button>

        {code && (
          <span className="rounded-lg bg-primary-50 px-3 py-1.5 font-mono text-lg tracking-widest text-primary-900">
            {code.code}
          </span>
        )}
      </div>

      {code && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          Type it into <strong>This phone</strong> in the app before{' '}
          {new Date(code.expiresAt).toLocaleTimeString('en-KE', {
            hour: '2-digit',
            minute: '2-digit',
          })}
          . After that, or after one handset uses it, ask for another.
        </p>
      )}

      {paired.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {paired.map((device) => (
            <li
              key={device.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-2.5 py-1.5 text-sm"
            >
              <span className="min-w-0">
                <span className="font-medium">{device.label}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {device.platform} {device.appVersion} · …{device.tokenLast4} ·{' '}
                  {device.lastSeenAt
                    ? `last seen ${new Date(device.lastSeenAt).toLocaleString('en-KE', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`
                    : 'never seen since pairing'}
                </span>
              </span>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-danger-700 hover:underline"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(device.id)}
              >
                Unpair
              </button>
            </li>
          ))}
        </ul>
      )}

      {devices.data && paired.length === 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          No handset has been paired yet, so every payment goes through the triggers below.
        </p>
      )}
    </div>
  );
}
